import { useQuery, useMutation } from "@vue/apollo-composable"
import gql from "graphql-tag"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { useRoute, useRouter } from "vue-router"

import { type AddressTypes } from "@/types"
import {
  type Child,
  type ChildList,
  Table,
  type QueryRootChildrenArgs,
  type MutationRootCreateChildArgs,
  type MutationRootRemoveChildrenArgs,
  type MutationRootUpdateChildArgs,
  type QueryRootChildByIdArgs,
  type CreateChildAddressInput,
  FunctionalRole,
  type CreateContractInput,
  type UpdateContractInput,
  type CreateCustodianInput,
  type CreateCustodianChildInput,
  type UpdateCustodianChildInput,
  type UpdateCustodianInput,
  type MutationRootCreateSiblingAssignmentArgs,
  type MutationRootRemoveSiblingAssignmentsArgs,
  type CreateSiblingAssignmentInput,
  TableFilterTable,
  type BookingTimeCategory,
  type BookingTypeEnum,
  type InstitutionFilter,
  Enrollment,
  type Booking,
} from "@/graphql/types"
import ServiceListQuery from "@/graphql/address/ServiceList.gql"
import BirthdayListQuery from "@/graphql/child/BirthdayList.gql"
import BookingTypeObjectFragment from "@/graphql/child/BookingTypeObjectFragment.gql"
import ChildByIdQuery from "@/graphql/child/ChildById.gql"
import CreateChildMutation from "@/graphql/child/CreateChild.gql"
import RemoveChildrenMutation from "@/graphql/child/RemoveChildren.gql"
import CreateSiblingMutation from "@/graphql/child/sibling/CreateSibling.gql"
import RemoveSiblingMutation from "@/graphql/child/sibling/RemoveSiblings.gql"
import SiblingAssignmentFragment from "@/graphql/child/sibling/SiblingAssignmentFragment.gql"
import UpdateChildMutation from "@/graphql/child/UpdateChild.gql"
import StateFragmentFull from "@/graphql/contract/StateFragmentFull.gql"
import BookingTimesfragment from "@/graphql/fragments/BookingTimesFragment.gql"
import { getTypeObjectProperty } from "@/utils/bookings"
import { calculateAge as calculateAgeAt } from "@/utils/calendar"
import { useApi } from "@/utils/composables/apollo/useApi"
import { useDataTableCache } from "@/utils/composables/useDataTableCache"
import { usePreset } from "@/utils/composables/usePreset"
import { calculateAge } from "@/utils/date"
import { extractIdSegment, type StrictEntityId } from "@/utils/entity"
import { eligibleForParentalFeeSubsidy as isEligibleForParentalFee } from "@/utils/kibig/children"
import ChildTableConfig from "@/views/children/children/ChildTableConfig"
import { apolloClient } from "@/vue-apollo"

import { type AddressListResult } from "./address"
import { useCharacteristicStore } from "./characteristic"
import { type ChildBookingAssignment } from "./childBookings"
import { type ContractGroupAssignment } from "./contractGroup"
import { type CustodianCategoryAssignment } from "./custodianCategory"
import { useCustomerStore } from "./customer"
import { useInstitutionStore } from "./institution"
import { useOrganizationStore } from "./organization"
import { useSessionStore } from "./session"

export type CreateSiblingResult = { createSiblingAssignment: Child[] }
export type RemoveSiblingResult = { removeSiblingAssignments: number }

export type ChildListResult = { children: ChildList }
export type ChildByIdResult = { childById: Child }
export type ChildCreateResult = { createChild: Child }
export type ChildUpdateResult = { updateChild: Child }
export type ChildRemoveResult = { removeChildren: number }

export type MappedChild = Child & {
  childAddressAssignements?: (CreateChildAddressInput & AddressTypes)[]
  childCustodianAssginments?: ChildCustodianAssignment
  custodianCategoryAssginments?: CustodianCategoryAssignment
  createContractInput?: Omit<CreateContractInput, "childId">
  childBookingAssignments?: ChildBookingAssignment[]
  childCharacteristicsIds?: Map<StrictEntityId, StrictEntityId>
  characteristicIds: StrictEntityId[]
  age?: number
  contractsRegistrationDates?: Date[]
  startOfContractDates?: Date[]
  endOfContractDates?: Date[]
  anzahl_geschwister?: number
  childGroups?: StrictEntityId[]
  childActionGroups?: StrictEntityId[]
  bookingsCategories?: BookingTypeEnum[]
  bookingHoursCategories?: BookingTimeCategory[]
  bookingRemarks?: string[]
  bookingComments?: string[]
  bookingSchools?: StrictEntityId[]
  bookingClasses: string[]
  bookingLunch?: boolean
  bookingPreCourse?: boolean
  bookingIsPreSchooler?: boolean
  bookingSlotSplitting?: boolean
  bookingEnrollmentPrematurely?: boolean
  bookingEnrollmentPostponed?: boolean
  bookingCommunes?: StrictEntityId[]
  fadNumbers?: string[]
  measlesProtectionFulfilled?: boolean
  eligibleForParentalFeeSubsidy?: boolean
}

export type ChildCustodianAssignment = {
  create?: {
    custodian: CreateCustodianInput
    childCustodian: Omit<CreateCustodianChildInput, "childId" | "custodianId">
    isContributor: boolean | undefined
  }
  update?: {
    custodian?: { id: StrictEntityId; input: UpdateCustodianInput }
    childCustodian?: { id: StrictEntityId; input: UpdateCustodianChildInput }
  }
}

export type ChildDraft = Partial<
  Child & {
    characteristicIds: StrictEntityId[]
    childCharacteristicsIds?: Map<StrictEntityId, StrictEntityId>
    groupAssignments: ContractGroupAssignment[]
    childAddressAssignements: (CreateChildAddressInput & AddressTypes)[]
    childAddressUnassignements: StrictEntityId[]
    childCustodianAssginments: ChildCustodianAssignment
    custodianCategoryAssginments: CustodianCategoryAssignment
    childBookingAssignments: ChildBookingAssignment[]
    groupAction: string
    bookingAction: string
    createContractInput: Omit<CreateContractInput, "childId">
    updateContractInput: UpdateContractInput
  }
>

export type FamilyMember = {
  kind: "child" | "custodian"
  id: string
  name: string
  relation: string
  selected?: boolean
}

export const childResultMap = {
  getList: (result: ChildListResult) => result.children,
  getById: (result: ChildByIdResult) => result.childById,
  getCreated: (result: ChildCreateResult) => result.createChild,
  getUpdated: (result: ChildUpdateResult) => result.updateChild,
  getRemovedCount: (result: ChildRemoveResult) => result.removeChildren,
  getLinkedCount: undefined,
}

export enum StepKey {
  Contract = "contract",
  General = "general",
  Miscellaneous = "miscellaneous",
  Vaccination = "vaccination",
  FirstGuardian = "firstGuardian",
  SecondGuardian = "secondGuardian",
  AdditionalGuardian = "additionalGuardian",
  Group = "group",
  Reservation = "reservation",
  Fee = "fee",
  Print = "print",
}

export type Step = {
  routeName: string
  required?: boolean
  disabled?: (completionState: Record<StepKey, boolean>) => boolean
  prevStepKey?: StepKey
  nextStepKey?: StepKey
  altNextStep?: {
    key: StepKey
    label: string
  }
}

export const useChildStore = defineStore(Table.Children, () => {
  const canCreateGroup = computed(() => sessionStore.hasRoles(["child:update", "group:update"]))

  const additionalGuardianEnabled = ref(false)
  const enableAdditionalGuardian = () => (additionalGuardianEnabled.value = true)
  const resetWizardToggles = () => (additionalGuardianEnabled.value = false)

  const siblingDataForChildCreate = ref<{ id: string; data: ChildDraft } | null>(null)
  const setSiblingDataForChildCreate = (payload: { id: string; data: ChildDraft }) => {
    siblingDataForChildCreate.value = payload
  }
  const useSiblingDataForChildCreate = () => {
    const value = siblingDataForChildCreate.value
    siblingDataForChildCreate.value = null
    return value
  }

  const stepMap = computed<Record<StepKey, Step>>(() => ({
    [StepKey.Contract]: {
      routeName: "ChildCreateFormContract",
      required: true,
      nextStepKey: StepKey.General,
    },
    [StepKey.General]: {
      routeName: "ChildCreateFormGeneral",
      required: true,
      prevStepKey: StepKey.Contract,
      nextStepKey: StepKey.Miscellaneous,
    },
    [StepKey.Miscellaneous]: {
      routeName: "ChildCreateFormMiscellaneous",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.General,
      nextStepKey: StepKey.Vaccination,
    },
    [StepKey.Vaccination]: {
      routeName: "ChildCreateFormVaccination",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.Miscellaneous,
      nextStepKey: StepKey.FirstGuardian,
    },
    [StepKey.FirstGuardian]: {
      routeName: "ChildCreateFormLocalGuardian0",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.Vaccination,
      nextStepKey: StepKey.SecondGuardian,
    },
    [StepKey.SecondGuardian]: {
      routeName: "ChildCreateFormLocalGuardian1",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.FirstGuardian,
      nextStepKey: StepKey.AdditionalGuardian,
    },
    [StepKey.AdditionalGuardian]: {
      routeName: "ChildCreateFormLocalGuardian2",
      disabled: (state) =>
        !state.contract ||
        !state.general ||
        (!additionalGuardianEnabled.value && !state[StepKey.AdditionalGuardian]),
      prevStepKey: StepKey.SecondGuardian,
      nextStepKey: StepKey.Group,
    },
    [StepKey.Group]: {
      routeName: "CreateChildGroupCreate",
      disabled: (state) => !state.contract || !state.general || !canCreateGroup.value,
      prevStepKey: additionalGuardianEnabled.value
        ? StepKey.AdditionalGuardian
        : StepKey.SecondGuardian,
      nextStepKey: StepKey.Reservation,
    },
    [StepKey.Reservation]: {
      routeName: "ChildCreateFormReservationCreate",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.Group,
      nextStepKey: StepKey.Fee,
    },
    [StepKey.Fee]: {
      routeName: "ChildCreateFormFee",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.Reservation,
      nextStepKey: StepKey.Print,
    },
    [StepKey.Print]: {
      routeName: "ChildCreatePrintContract",
      disabled: (state) => !state.contract || !state.general,
      prevStepKey: StepKey.Fee,
    },
  }))
  const sessionStore = useSessionStore()
  const characteristicStore = useCharacteristicStore()
  const customerStore = useCustomerStore()
  const organizationStore = useOrganizationStore()
  const institutionStore = useInstitutionStore()
  const router = useRouter()
  const route = useRoute()

  const presetConf = usePreset(
    Table.Children,
    ChildTableConfig.columnDefs,
    ["ChildrenPage", "dashboard", "AttendancePage", "CheckInPage", "ChildEdit", "CustodiansPage"],
    TableFilterTable.Children,
    ChildTableConfig.filterableColumnDefs
  )

  const listQueryVariables = computed(() => ({
    context: sessionStore.institutionFilter,
    includeCharacteristics: sessionStore.hasRoles(["characteristic:list"]),
    includeGroup: sessionStore.hasRoles(["group:list"]),
    includeFeeType: sessionStore.hasRoles(["fee:list"]),
    includeVaccination: sessionStore.hasRoles([
      "child:view",
      FunctionalRole.ChildAllowSensitiveData,
    ]),
    includeContract: sessionStore.hasRoles(["contract:list"]),
    includeBooking: sessionStore.hasRoles(["child:view", "booking:list"]),
  }))

  const viewQueryVariables = computed(() => ({
    includeCharacteristics: sessionStore.hasRoles(["characteristic:view"]),
    includeGroup: sessionStore.hasRoles(["group:view"]),
    includeVaccination: sessionStore.hasRoles([FunctionalRole.ChildAllowSensitiveData]),
    includeContracts: sessionStore.hasRoles(["contract:view"]),
    includeCustodians: sessionStore.hasRoles(["child:view"]),
    includeFeeType: sessionStore.hasRoles(["fee:view"]),
    includeAddress: sessionStore.hasRoles(["address:view"]),
    includeYouthWelfareOffice: sessionStore.hasRoles(["youth_welfare_office:view"]),
    includeBookings: sessionStore.hasRoles(["booking:view"]),
  }))

  const listQueryOptions = computed(() => ({
    enabled:
      sessionStore.hasRoles(["child:list"]) &&
      !!sessionStore.institutionFilter.institution &&
      !!presetConf.queryInfo.queryFieldsLoaded,
  }))

  const PresetBuiltQuery = computed(
    () => gql`
      query ChildList($context: ContextFilter,$includeVaccination:Boolean!,$includeBooking:Boolean!,$includeContract:Boolean!,$includeGroup:Boolean!) {
        children(context: $context) {
          page
          limit
          total
          items {
            id
            n
            childId
            altChildId
            ${presetConf.queryInfo.queryFields}
            characteristics {
              id
              childId
              characteristicId
              child {
                id
              }
              characteristic {
                id
              }
            }
            person {
              firstname
              lastname
            }
            birth {
              date
            }
            siblings  {
              ...SiblingAssignmentFragment
            }

            contracts  @include(if: $includeContract){
              id
              state {
                ...StateFragmentFull
              }
              registrationDate
              startOfContract
              endOfContract
              groups @include(if: $includeGroup) {
                id
                group {
                  id
                }
                movement {
                  entry
                  exit
                }
              }
              remarks
              bookings  @include(if: $includeBooking){
                id
                contractId
                contract {
                  id
                  startOfContract
                  endOfContract
                }
                from
                to
                nurseryChild
                migration
                assistedIntegration
                isSchoolChild
                lunch
                preCourse
                isPreSchooler
                schoolData {
                  schoolId
                }
                times {
                  ...BookingTimesFragment
                }
                timeFactorManuallySet
                type
                typeObject {
                  ...BookingTypeObjectFragment
                }
              }
            }
            vaccination @include(if: $includeVaccination){
              status
              firstConfirmationDate
              secondConfirmationDate
              contraindicationEndDate
            }
          }
        }
      }

      ${SiblingAssignmentFragment}
      ${StateFragmentFull}
      ${BookingTimesfragment}
      ${BookingTypeObjectFragment}
    `
  )

  const api = useApi<
    Child,
    "children",
    ChildListResult,
    QueryRootChildrenArgs,
    ChildByIdResult,
    QueryRootChildByIdArgs,
    ChildCreateResult,
    MutationRootCreateChildArgs,
    ChildUpdateResult,
    MutationRootUpdateChildArgs,
    ChildRemoveResult,
    MutationRootRemoveChildrenArgs
  >({
    typename: "Child",
    operations: {
      list: PresetBuiltQuery,
      getById: ChildByIdQuery,
      create: CreateChildMutation,
      update: UpdateChildMutation,
      remove: RemoveChildrenMutation,
      link: undefined,
    },
    resultMap: childResultMap,
    mapRemovedIds: (variables) => variables.ids,
    listQueryVariables,
    listQueryOptions,
    mutationVariables: viewQueryVariables,
    getByIdAdditionalVariables: viewQueryVariables,
  })

  const toMapped = (child: Child): MappedChild => ({
    ...child,
    characteristicIds: (child?.characteristics ?? []).reduce<StrictEntityId[]>(
      (prev, assignment) =>
        assignment.characteristic?.id ? (prev.push(assignment.characteristic.id), prev) : prev,
      []
    ),
    childCharacteristicsIds: new Map(
      child?.characteristics?.map((assignment) => [assignment.characteristic?.id, assignment.id]) ??
        []
    ),
    birth: {
      ...child?.birth,
      ...(child?.birth?.date ? { date: new Date(child.birth.date) } : {}),
    },
    ...(child?.birth?.date ? { age: calculateAge(child.birth.date) } : {}),
    contractsRegistrationDates: (child?.contracts ?? []).reduce<Date[]>(
      (dates, contract) =>
        contract?.registrationDate
          ? (dates.push(new Date(contract.registrationDate)), dates)
          : dates,
      []
    ),
    startOfContractDates: (child?.contracts ?? []).reduce<Date[]>(
      (dates, contract) =>
        contract?.startOfContract ? (dates.push(new Date(contract.startOfContract)), dates) : dates,
      []
    ),
    endOfContractDates: (child?.contracts ?? []).reduce<Date[]>(
      (dates, contract) =>
        contract?.endOfContract ? (dates.push(new Date(contract.endOfContract)), dates) : dates,
      []
    ),
    anzahl_geschwister: (child?.externalSiblings ?? []).length + (child?.siblings ?? []).length,

    childGroups: (child?.contracts ?? []).flatMap(
      ({ groups }) => groups?.map((grp) => grp.group.id) ?? []
    ),
    childActionGroups: (child?.contracts ?? []).flatMap(
      ({ groups }) =>
        groups?.filter((grp) => grp?.group?.actionGroup).map((grp) => grp.group.id) ?? []
    ),
    bookingsCategories: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<
      BookingTypeEnum[]
    >((acc, booking) => (acc.push(booking.type), acc), []),

    bookingHoursCategories: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<
      BookingTimeCategory[]
    >(
      (acc, booking) =>
        booking?.timeFactor?.category ? (acc.push(booking.timeFactor.category), acc) : acc,
      []
    ),
    bookingRemarks: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<string[]>(
      (acc, booking) => (booking?.remarks ? (acc.push(booking.remarks), acc) : acc),
      []
    ),
    bookingComments: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<string[]>(
      (acc, booking) => (booking.times?.comments ? (acc.push(booking.times?.comments), acc) : acc),
      []
    ),
    bookingSchools: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<StrictEntityId[]>(
      (acc, booking) =>
        booking?.schoolData?.school?.id ? (acc.push(booking.schoolData.school.id), acc) : acc,
      []
    ),
    bookingClasses: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<string[]>(
      (acc, booking) =>
        booking?.schoolData?.className ? (acc.push(booking.schoolData.className), acc) : acc,
      []
    ),
    bookingLunch: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => booking?.lunch === true
    ),
    bookingPreCourse: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => booking?.preCourse === true
    ),
    bookingIsPreSchooler: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => booking?.isPreSchooler === true
    ),
    bookingSlotSplitting: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => !!getTypeObjectProperty(booking?.typeObject, "slotSplitting")
    ),
    bookingEnrollmentPrematurely: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => (booking as Booking)?.preSchooler === Enrollment.Prematurely
    ),
    bookingEnrollmentPostponed: (child?.contracts?.flatMap((c) => c.bookings) ?? []).some(
      (booking) => (booking as Booking)?.preSchooler === Enrollment.Postponed
    ),
    bookingCommunes: (child?.contracts?.flatMap((c) => c.bookings) ?? []).reduce<StrictEntityId[]>(
      (acc, booking) => {
        const communeId = getTypeObjectProperty(booking?.typeObject, "communeId")
        return communeId ? (acc.push(communeId), acc) : acc
      },
      []
    ),
    fadNumbers: [
      child?.financialAccountingData?.fadNumber ?? "",
      ...(child?.custodies ?? []).map(
        (c) => c?.custodian?.financialAccountingData?.fadNumber ?? ""
      ),
      ...(child?.contracts ?? []).flatMap((contract) =>
        (contract?.mainDebtors ?? []).map(
          (md) => md?.custody?.custodian?.financialAccountingData?.fadNumber ?? ""
        )
      ),
      ...(child?.contracts ?? []).flatMap((contract) =>
        (contract?.debtors ?? []).map(
          (d) => d?.custody?.custodian?.financialAccountingData?.fadNumber ?? ""
        )
      ),
    ]
      .map((v) => (v ?? "").toString().trim())
      .filter((v) => v.length > 0),
    measlesProtectionFulfilled: /^J/.test(child?.vaccination?.status ?? ""),
    eligibleForParentalFeeSubsidy: (() => {
      if (!child?.birth?.date) return false
      const now = new Date()
      const isSchool = (child.contracts ?? []).some(({ bookings }) =>
        (bookings ?? []).some((booking) => booking.isSchoolChild)
      )
      const completedAtEoY = calculateAgeAt(child.birth.date, new Date(now.getFullYear() + 1, 0, 1))
      return isEligibleForParentalFee(completedAtEoY, now.getMonth(), isSchool)
    })(),
  })

  const list = computed<MappedChild[]>(() => (api.listResult?.children.items ?? []).map(toMapped))
  const listResult = computed(() =>
    listQueryOptions.value.enabled ? api.listResult?.children.items : undefined
  )

  const birthdayListOptions = computed(() => ({
    enabled:
      sessionStore.hasRoles(["child:list"]) && !!listQueryVariables.value.context?.institution,
  }))

  const {
    result: birthdaysResult,
    loading: birthdaysLoading,
    refetch: birthdayRefetch,
  } = useQuery<{
    birthdays: ChildList
  }>(
    BirthdayListQuery,
    computed(() => ({
      ...listQueryVariables.value,
    })),
    birthdayListOptions
  )

  const birthdayList = computed(() => {
    if (birthdayListOptions.value.enabled) {
      return birthdaysResult?.value?.birthdays?.items ?? []
    }
    return []
  })

  const enabled = computed(() => ({
    enabled:
      sessionStore.hasRoles(["address:list"]) &&
      !!sessionStore.institutionFilter.institution &&
      route.meta?.allowServiceList === true,
  }))

  const { result: serviceResult } = useQuery<AddressListResult>(
    ServiceListQuery,
    computed(() => ({
      context: sessionStore.institutionFilter,
    })),
    enabled
  )
  const serviceList = computed(() => serviceResult.value?.addresses?.items ?? [])

  customerStore.api.addRemoveReducer(
    api.getListFilterRemoveReducer(
      (ids) => (item) => !ids.find((cid) => cid === extractIdSegment(item.id, "cid"))
    )
  )
  organizationStore.api.addRemoveReducer(
    api.getListFilterRemoveReducer(
      (ids) => (item) => !ids.find((oid) => oid === extractIdSegment(item.id, "oid"))
    )
  )
  institutionStore.api.addRemoveReducer(
    api.getListFilterRemoveReducer(
      (ids) => (item) => !ids.find((iid) => iid === extractIdSegment(item.id, "iid"))
    )
  )
  characteristicStore.api.addRemoveReducer(() => (cache, result) => {
    if (result?.data?.removeCharacteristics)
      cache.evict({ fieldName: "childById", broadcast: false })
  })
  api.addRemoveReducer(api.getListFilterRemoveReducer((ids) => (item) => !ids.includes(item.id)))

  const dataTableCache = useDataTableCache(list)

  function discardForm() {
    void router.push({ name: "ChildrenPage" })
  }

  const createMultipleSiblingLoading = ref(false)

  const siblingMutationsLoading = computed(
    () =>
      createMultipleSiblingLoading.value ||
      createSiblingLoading.value ||
      removeSiblingsLoading.value
  )

  const { mutate: createSibling, loading: createSiblingLoading } = useMutation<
    CreateSiblingResult,
    MutationRootCreateSiblingAssignmentArgs
  >(CreateSiblingMutation, {
    errorPolicy: "all",
  })

  const { mutate: removeSiblings, loading: removeSiblingsLoading } = useMutation<
    RemoveSiblingResult,
    MutationRootRemoveSiblingAssignmentsArgs & { childId: StrictEntityId }
  >(RemoveSiblingMutation, {
    errorPolicy: "all",
    refetchQueries: ["ChildList", "ChildById"],
  })

  async function linkSiblings<T extends { id: StrictEntityId; siblings?: Child[] | null }>(
    children: T[],
    context: InstitutionFilter | undefined = undefined
  ) {
    const siblingPairs: CreateSiblingAssignmentInput[] = []
    for (let i = 1; i < children.length; i++) {
      if (!(children[0].siblings ?? []).map((sibling) => sibling?.id).includes(children[i].id))
        siblingPairs.push({
          firstChild: children[0].id,
          secondChild: children[i].id,
        })
    }
    if (siblingPairs.length === 0) return true
    createMultipleSiblingLoading.value = true
    try {
      const createSiblingAssignment = (input: CreateSiblingAssignmentInput) =>
        apolloClient.mutate<CreateSiblingResult, MutationRootCreateSiblingAssignmentArgs>({
          mutation: CreateSiblingMutation,
          variables: {
            context: context ?? sessionStore.institutionFilter,
            input,
          },
          errorPolicy: "all",
        })

      await siblingPairs.reduce<Promise<void>>((chain, input) => {
        return chain.then(async () => {
          const result = await createSiblingAssignment(input)
          if (!result.data?.createSiblingAssignment) {
            throw new Error("Failed to create sibling assignment")
          }
        })
      }, Promise.resolve())

      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      createMultipleSiblingLoading.value = false
    }
  }

  async function getChildById(id: string): Promise<Child | undefined> {
    try {
      const result = await apolloClient.query<ChildByIdResult>({
        query: ChildByIdQuery,
        variables: {
          id,
          ...viewQueryVariables.value,
        },
        fetchPolicy: "network-only",
      })
      if (result) return result.data?.childById
    } catch (err) {
      console.error(err)
    }
  }

  async function refetchChildList() {
    await api.refetch?.()
  }

  return {
    api,
    list,
    listResult,
    refetchChildList,
    toMapped,
    dataTableCache,
    stepMap,
    listQueryVariables,
    viewQueryVariables,

    birthdayList,
    birthdaysLoading,
    birthdayRefetch,

    presetConf: () => presetConf,

    discardForm,

    siblingMutationsLoading,
    linkSiblings,
    createSibling,
    removeSiblings,
    getChildById,
    serviceList,

    additionalGuardianEnabled,
    enableAdditionalGuardian,
    resetWizardToggles,
    setSiblingDataForChildCreate,
    useSiblingDataForChildCreate,
  }
})
